import { useCallback, useEffect, useRef, useState } from "react";

interface UseScrollCollapseOptions {
  /** Disable auto-expand/collapse (manual toggle only) */
  enabled?: boolean;
}

/**
 * Hook that auto-expands a filter panel when the user is at the top
 * and auto-collapses when they scroll down.
 *
 * Uses IntersectionObserver on a sentinel element — fires once on
 * threshold crossing (not continuously), avoiding the feedback loop
 * that scroll listeners cause when state changes shift content height.
 *
 * Manual toggles are respected — if the user opens filters while scrolled
 * down, the hook won't fight them until the sentinel re-enters the viewport.
 *
 * Usage: render <div ref={sentinelRef} className="h-px w-full" /> above
 * the sticky header (must be 1px+ for IntersectionObserver to work).
 */
export function useScrollCollapse({
  enabled = true,
}: UseScrollCollapseOptions = {}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isCompact, setIsCompact] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const userOverrideRef = useRef(false);
  const transitionLockRef = useRef(false);
  const isCompactRef = useRef(false);

  // Manual toggle — sets override flag so auto-collapse doesn't fight the user
  const toggle = useCallback(() => {
    setIsExpanded((prev) => {
      const next = !prev;
      userOverrideRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled || !sentinelRef.current) return;

    // Use the explicitly tagged scroll container, or fall back to DOM walk
    const el = sentinelRef.current;
    let root: HTMLElement | null =
      el.closest("[data-scroll-container]") as HTMLElement | null;

    if (!root) {
      root = el.parentElement;
      while (root) {
        if (root === document.documentElement || root === document.body) {
          root = null;
          break;
        }
        const ov = getComputedStyle(root).overflowY;
        if (ov === "auto" || ov === "scroll" || ov === "overlay") break;
        root = root.parentElement;
      }
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Ignore events during transition lock to prevent oscillation.
        // When compact mode changes padding/height, the sentinel can bounce
        // across the threshold boundary — the lock prevents re-triggering
        // until the CSS transition settles.
        if (transitionLockRef.current) return;

        const visible = entry.isIntersecting;

        // Only act on actual state changes
        const currentlyCompact = !visible;
        if (currentlyCompact === isCompactRef.current) return;

        transitionLockRef.current = true;
        setTimeout(() => { transitionLockRef.current = false; }, 400);

        if (visible) {
          // Sentinel visible → user is at/near the top → expand
          userOverrideRef.current = false;
          isCompactRef.current = false;
          setIsCompact(false);
          setIsExpanded(true);
        } else {
          // Sentinel scrolled out of view → compact + collapse
          isCompactRef.current = true;
          setIsCompact(true);
          if (!userOverrideRef.current) {
            setIsExpanded(false);
          }
        }
      },
      { root, threshold: 1.0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled]);

  return { isExpanded, isCompact, toggle, sentinelRef };
}
